import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Button,
  Box
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { CustomButtonn } from '../../../../../components/Input/CustomButton';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../../../../../utils';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const DETAIL_HEADER = [
  {
    header: 'Date',
    accessor: (row) => formatDate(row?.date)
  },
  {
    header: 'Patient Name',
    accessor: (row) => `${row?.patient?.name_suffix} ${row?.patient?.first_name} ${row?.patient?.last_name} ` && row?.patient_name
  },
  {
    header: 'Service Name',
    accessor: (row) => row?.services?.service_name
  }
];

const TABLE_HEADER = [
  {
    header: 'Report No.',
    accessor: 'report_no'
  },
  {
    header: 'Report Date',
    accessor: (row) => formatDate(row?.date)
  },
  {
    header: 'UHID',
    accessor: 'uhid'
  },
  {
    header: 'IPD No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Patient Name',
    accessor: 'patient_name'
  },
  {
    header: 'Service Name',
    accessor: (row) => row?.service_name?.service_name
  }
];

const initialIndoorRegisterationDataObj = {
  data: [],
  loading: false
};

export const ProcedureReport = () => {
  const [detailList, setDetailList] = useState([]);
  const [diagnosisDropdown, setDiagnosisDropdown] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [indoorRegisterationDataObj, setIndoorRegisterationDataObj] = useState(initialIndoorRegisterationDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [input, setInput] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const [service, setService] = useState([]);
  const [templateData, setTemplateData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [editorData, setEditorData] = useState('');
  const [serviceDropdown, setServiceDropdown] = useState([]);
  const [templatesMasterDropdown, setTemplatesMasterDropdown] = useState([]);
  const [prevTemplateData, setPrevTemplateData] = useState(null);  // Store the previous state
  const [radiologyAuthorisedDoctor,setRadiologyAuthorisedDoctor] = useState([])
  const isEnabledDate =
    user && user.permissions ? user.permissions.some((permission) => permission.includes('DatePermission')) : false;

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        diagnosis_name: Yup.string().required(' '),
        rep_doctor: Yup.string().required(' '),
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: new Date().toLocaleTimeString('en-US', { hour12: false }).slice(0, 5),
      doc_date: new Date().toISOString().split('T')[0]
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const formSubmit = async (formValue) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    try {
      const response = await request(
        editFlag ? '/radiology-procedure-report-update' : '/radiology-procedure-report-create',
        {
          method: editFlag ? 'PUT' : 'POST',
          params: editFlag ? selectedRecord._id : null,
          body: {
            ...formValue,
            editorContent: Array.isArray(editorData) ? editorData.join(' ') : editorData,
            createdBy: user.name,
            updatedBy: editFlag ? user.name : '',
            organisation
          }
        }
      );
      if (response?.success) {
        toast(`Successfully ${editFlag ? 'updated' : 'created'} Patient Discharge`, { variant: 'success' });
        setSelectedRecord(response?.data);
        setCurrentAction();
        searchIPDDetails();
    
      } else {
        toast(`Failed to ${editFlag ? 'update' : 'create'} Patient Discharge`, { variant: 'error' });
      }
    } catch (error) {
      toast(`An error occurred: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/radiology-procedure-report-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Procedure Report', { variant: 'success' });
      setSelectedRecord();
      searchIPDDetails();
    } else {
      toast('Failed to delete Procedure Report', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setIndoorRegisterationDataObj({ loading: true });
    const response = await request('/radiology-procedure-report-list', { method: 'GET' });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setIndoorRegisterationDataObj({ loading: false, data: sortedData });
      setOriginalData(sortedData);
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setIndoorRegisterationDataObj({ loading: false });
    }
  };

  const handleUhidSearch = async () => {
    const lowercasedInput = input.toLowerCase().trim();
   
      try {
        const response = await request('/radiology-procedure-report-list', {
          method: 'GET',
          query: { patient_name: lowercasedInput }
        });
        if (response.success && Array.isArray(response.data)) {
          setIndoorRegisterationDataObj({ loading: false, data: response.data });
        } else {
          toast('Error in fetching data', { variant: 'error' });
          setIndoorRegisterationDataObj({ loading: false, data: [] });
        }
      } catch (error) {
        console.error('Error during search:', error);
        toast('Error during search', { variant: 'error' });
        setIndoorRegisterationDataObj({ loading: false, data: [] });
      }
 
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
    handleUhidSearch();
  };

  useEffect(() => {
    if (detailList && detailList.length > 0) {
      setPatientData(detailList);
      setIsFiltered(false);
    }
  }, [detailList]);

  const handlePatientSearch = () => {
    const filtered = detailList.filter(
      (item) =>
        item.patient.first_name === input ||
        item.uhid.toString().includes(input) ||
        (item.ipd_no && item.ipd_no.toString().includes(input))
    );
    setPatientData(filtered);
  };

  const handlePatientChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
    handlePatientSearch();
  };

  useEffect(() => {
    searchIPDDetails();
  }, []);

const searchIPDDetails = async () => {
  setLoading(true);
  const selectedRadio = "procedure_done"
  try {
    const response = await request('/service-acknowledgement-radiology-list', { method: 'GET' ,query:{selectedRadio}});
    if (response?.success) {
      const data = response.data || [];
        setDetailList(data);
        setPatientData(data);
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
  } catch (error) {
    console.error('Error fetching patient details:', error);
    toast('An error occurred while fetching patient details', { variant: 'error' });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', 'status', '__v']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('service_name', selectedRecord?.service_name._id);
      setEditorData(selectedRecord?.editorContent);
      setValue('diagnosis_name',selectedRecord?.diagnosis_name?._id)
      const formattedDate = selectedRecord.doc_date
        ? new Date(selectedRecord.doc_date).toISOString().split('T')[0]
        : '';
      setValue('doc_date', formattedDate);
      if (selectedRecord.time) {
        setValue('time', selectedRecord.time);
      }
    } 
  }, [setValue, selectedRecord, editorData]);

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  const getDiagnosisDropdown = useCallback(async () => {
    const data = await dropdownFetch('radiology-diagnosis-master');
    setDiagnosisDropdown(data);
  }, []);

  const getServiceDropdown = useCallback(async () => {
    const data = await dropdownFetch('service');
    setServiceDropdown(data);
  }, []);

  const getTemplateMasterDropdown = useCallback(async () => {
    const data = await dropdownFetch('radiology-template');
    setTemplatesMasterDropdown(data);
  }, []);

  useEffect(() => {
    getDiagnosisDropdown();
    getServiceDropdown();
    getTemplateMasterDropdown();
  }, [getDiagnosisDropdown, getServiceDropdown, getTemplateMasterDropdown]);

  const handleActiveTab = (newTabIndex) => {
    setActiveTab(newTabIndex);
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setEditorData(data);
  };


  const handleAuthorize = async () => {
    setLoading(true);
    try {
      const response = await request('/radiology-procedure-report-authorize', {
        method: 'PUT',
        params: selectedRecord?._id
      });
      if (response?.success && response.data) {
        toast(`Successfully updated Radiology Acknowledgement`, { variant: 'success' });
        setSelectedRecord(response.data);
      
      } else {
        console.error(response.message || 'Failed to update report type');
        toast(`Failed to update Radiology Acknowledgement`, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting the form:', error);
      toast('An error occurred while processing your request', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };


  const fetchTemplate = async (service) => {
    try {
      const response = await request('/radiology-template-list', {
        method: 'GET',
        query: { service },
      });
      if (response?.success && response?.data?.length > 0) {
        const template = response.data[0]; 
        setTemplateData(template)
      } else {
        console.error(response?.message || 'Failed to fetch template');
        toast("Failed to Fetch Template", { variant: 'error' });
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      toast("An error occurred while processing your request", { variant: 'error' });
    } 
  };

  
useEffect(()=>{
  if(templateData){
    setEditorData(templateData.editorContent)
    setValue('template_name',templateData?.description)
  }
},[templateData])
  

const getRadiologyDoctor = useCallback(async () => {
  const data = await dropdownFetch('radiology-authority-doctor');
  setRadiologyAuthorisedDoctor(data);
}, []);


useEffect(()=>{
  getRadiologyDoctor()
},[getRadiologyDoctor])
 

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Procedure Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn id={'Exit'} variant="contained">
                  Exit
                </CustomButtonn>
                <CustomButtonn id={'Submit'} variant="contained" onClick={() => handleAuthorize()}>
                  Authorize
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ borderBottom: '0px solid', borderColor: 'divider' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(0)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 700
                      }}
                    >
                      Patient Detail
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(1)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 700
                      }}
                    >
                      Result
                    </Button>
                  </Box>
                </Grid>

                {activeTab === 0 && (
                  <>
                    <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                          <MuiTextField
                            sx={{
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                padding: '0',
                                border: '1px solid #1976d2', // Set the border color and width here
                                borderRadius: '8px', // Set the border radius (optional)
                                '&:hover': {
                                  borderColor: '#1565c0' // Border color on hover (optional)
                                },
                                '&.Mui-focused': {
                                  borderColor: '#0d47a1' // Border color when focused (optional)
                                }
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '8px 14px',
                                fontSize: '1.4rem' // Increase font size
                              }
                            }}
                            label="Search Patient"
                            variant="outlined"
                            value={input}
                            onChange={handlePatientChange}
                          />
                        </Stack>
                        <Table
                          disabled
                          containerSX={{
                            flex: 1,
                            border: '0px solid rgba(0,0,0,0.27)',
                            minHeight: '500px', 
                            width: '100%', 
                            overflowY: 'auto' 
                          }}
                          data={currentAction === 'new' ? patientData : []}
                          columns={DETAIL_HEADER}
                          size="small"
                          onRowClick={(row) => {
                            console.log(row, 'ROW IS HERE !');
                            setValue('doc_no', row?.doc_no);
                            setValue('uhid', row?.uhid);
                            setValue('ipd_no', row?.ipd_no);
                            setValue('service_name',row?.services?.service)
                            setValue('sponsor',row?.patient?.sponsor?.name || row?.sponsorinfo?.name)
                            setValue('gender',row?.patient?.gender || row.gender)
                            setValue('age', calculateAge(row?.patient?.dob) || row.age)
                            setValue('consultant',row?.patient?.consultant?.name)
                            setValue(
                              'patient_name',
                              `${row?.patient?.name_suffix} ${row?.patient?.first_name} ${row?.patient?.last_name}`
                              && row?.patient_name
                            );
                            fetchTemplate(row?.services?.service)
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={6} sx={{ display: 'flex', gap: 0.2, flexDirection: 'column' }}>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Dignosis Name</Typography>
                        <SelectTag
                          formControlSX={{
                            flex: 0.8,
                            border: currentAction ? '0.5px solid red' : ''
                          }}
                          name="diagnosis_name"
                          placeholder="Select Diagnosis"
                          menus={diagnosisDropdown.map((data) => ({
                            ...data,
                            name: data.diagnosis
                          }))}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Date/Time</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Doc No." name="date" type="date" />
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Doc No." name="time" />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Report No.</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Report No." name="report_no" />
                      </Stack>

                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doc No.</Typography>
                        <TextField disabled sx={{ flex: 0.4 }} placeholder="Doc No." name="doc_no" />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doc Date</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Doc date" name="doc_date" type="date" />
                      </Stack>

                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>UHID/Ipd No.</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="uhid" name="uhid" />
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="uhid" name="ipd_no" />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Patient Name</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Patient Name" name="patient_name" />
                      </Stack>

                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Gender/Age</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Gender" name="gender" />
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="Age" name="age" />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Consultant</Typography>
                        <TextField disabled sx={{ flex: 0.2 }} placeholder="consultant" name="consultant" />
                      </Stack>

                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sponsor</Typography>
                        <TextField disabled sx={{ flex: 0.4 }} placeholder="Sponsor" name="sponsor" />
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Rep. Doctor</Typography>
                        <SelectTag 
                        disabled={!['new','edit'].includes(currentAction)} 
                        formControlSX={{ flex: 0.2 }} 
                        placeholder="Rep. Doctor"
                         name="rep_doctor"
                         menus={radiologyAuthorisedDoctor.map((data) => ({ ...data, name: data.doctor }))}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service Name</Typography>
                        <SelectTag
                          formControlSX={{ flex: 0.8 }}
                          placeholder="Service name"
                          name="service_name"
                          menus={serviceDropdown.map((data) => ({ ...data, name: data.service_name, _id: data._id }))}
                        />
                      </Stack>
                      <Stack spacing={0.5} direction="row" alignItems="center">
                        <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Template Name</Typography>
                        <TextField sx={{ flex: 0.8 }} name="template_name" disabled placeholder="Template Name" />
                      </Stack>
                    </Grid>
                  </>
                )}

                {activeTab === 1 && (
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <CKEditor
                      editor={ClassicEditor}
                      data={Array.isArray(editorData) ? editorData.join(' ') : editorData || ''}
                      onReady={(editor) => {}}
                      onChange={handleEditorChange}
                      onBlur={(event, editor) => {
                        console.log('Blur.', editor);
                      }}
                      onFocus={(event, editor) => {
                        console.log('Focus.', editor);
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Procedure Report Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem' 
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' 
              }
            }}
            label="Search Patient"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 1 }}
          columns={TABLE_HEADER}
          data={indoorRegisterationDataObj.data || []}
          loading={indoorRegisterationDataObj.loading}
          onRowClick={(row) => {
            delete row.patient;
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
